import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import useFetch from '../../hooks/useFetch';
import { Page, PageTitle, DataTable, SelectLocation, SelectStaff, ActionBar, Sheet } from '../../components';
import InlineLoader from '../../loading/InlineLoader';
import { Checkbox, Select, MenuItem, FormControl, InputLabel, Grid, Typography, Alert, Divider } from '@mui/material';
import { STAFF, LOCATIONS } from '../../constants';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { dateLocaleFor } from '../../i18n/formats';
import { tokens } from '../../theme'

export default function PayCommissions(props) {
  const { setAuth } = props;
  const { isPending, fetchResource, showAlert, alertMessage, setAlertMessage, setShowAlert } = useFetch({ setAuth });
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [showChangeLocation, setShowChangeLocation] = useState(false);
  const [showChangeStaff, setShowChangeStaff] = useState(false);
  const [sales, setSales] = useState({});
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  function saveLocation(locationToSet) {
    localStorage.setItem('location', locationToSet);
    setLocation(locationToSet);
    setShowChangeLocation(false);
  };

  function selectStaff(staffToSelect) {
    localStorage.setItem('selectedStaff', staffToSelect);
    setSelectedStaff(staffToSelect);
    setShowChangeStaff(false);
  }

  async function unpaidCommissions() {
    const response = await fetchResource({ resource: 'commissions/unpaid-com-sales', body: { location: location }, loadingKey: 'sales' });
    if (!response.sales) throw new Error(response.error);
    setSales(response.sales);
  };

  function handleCheckboxChange(id, staff, checked) {
    setSales(prev => {
      const updatedSales = { ...prev };
      updatedSales[staff] = updatedSales[staff].map(sale => {
        if (sale.id === id) return { ...sale, paid: checked };
        return sale;
      });
      return updatedSales;
    });
  };

  async function handleSubmitPayout() {
    setShowConfirmSubmit(false);

    try {
      const response = await fetchResource({ resource: 'commissions/payout-com-sales', body: { location, sales, staff: selectedStaff }, blocking: true });
      if (response.result !== 'OK') throw new Error(response.error);
      setAlertMessage({message: t('commissions.pay.paySuccess'), type: 'success'});
      setShowAlert(true);
      await unpaidCommissions();
    } catch (error) {
      setAlertMessage({message: error.message, type: 'error'});
      setShowAlert(true);
    }
  }

  useEffect(() => {
    if (!location) return;
    localStorage.setItem('location', location);
    unpaidCommissions();
  }, [location]);

  useEffect(() => {
    if (!selectedStaff) return;
    localStorage.setItem('selectedStaff', selectedStaff);
  }, [selectedStaff]);

  useEffect(() => {
    const storedLocation = localStorage.getItem('location');
    if (!storedLocation) return setShowChangeLocation(true);
    setLocation(storedLocation);
  }, []);

  useEffect(() => {
    const storedStaff = localStorage.getItem('selectedStaff');
    if (!storedStaff) return setShowChangeStaff(true);
    setSelectedStaff(storedStaff);
  }, []);

  const Header = () => (
    <Grid container position='relative'>
        <Grid container position='absolute' mt={1} justifyContent='space-between'>
          <Grid item>
            <LocationOnIcon
              onClick={() => setShowChangeLocation(true)}
              sx={{ cursor: 'pointer', margin: '5px' }}
            />
            <SupervisorAccountIcon
              onClick={() => setShowChangeStaff(true)}
              sx={{ cursor: 'pointer', margin: '5px' }}
            />
          </Grid>
          <Grid item>
            <TaskAltIcon
                onClick={() => {
                  if (!Object.keys(sales).length) return;
                  if (!Object.values(sales).flat().some(sale => sale.paid)) return;
                  setShowConfirmSubmit(true);
                }}
                sx={{ cursor: 'pointer', margin: '5px' }}
            />
          </Grid>
        </Grid>
        <Grid item width='100%'>
            <PageTitle>{t('commissions.pay.title')} - <b>{location && LOCATIONS[location]}</b></PageTitle>
        </Grid>
    </Grid>
  )

  const paySaleCheckbox = params => (
    <Checkbox
      checked={!!params.value}
      disabled={params.row.giftCardPayment}
      onChange={e => handleCheckboxChange(params.id, params.row.attributedStaff, e.target.checked)}
    />
  )

  const ChangeLocationPopup = () => (
    <SelectLocation
        location={location}
        saveLocation={saveLocation}
        showChangeLocation={showChangeLocation}
        setShowChangeLocation={setShowChangeLocation}
    />
  )

  const ChangeStaffPopup = () => {
    if (!location) return;
    return (
      <SelectStaff
        selectedStaff={selectedStaff}
        selectStaff={selectStaff}
        showChangeStaff={showChangeStaff}
        setShowChangeStaff={setShowChangeStaff}
        location={location}
      />
    )
  }

  const AllPaid = () => {
    if (isPending('sales')) return <InlineLoader />;
    return (
      <Grid container justifyContent='center' alignItems='center' height='100%'>
        <Typography variant='h6'>{t('commissions.noneToPay')}</Typography>
      </Grid>
    )
  }

  const SalesTables = () => {
    if (!location) return null;
    if (!selectedStaff) return null;

    if (!sales[selectedStaff]) return (
      <Grid container justifyContent='center' alignItems='center' width='100%' height='100%'>
        <Grid item>
          <Typography variant='h6'>{t('commissions.pay.noneForStaff', { staff: selectedStaff })}</Typography>
        </Grid>
      </Grid>
    )

    const totalToPay = sales[selectedStaff].reduce((acc, sale) => {
      if (!sale.paid) return acc;
      return acc + sale.payable;
    }, 0);

    const commission = Math.round(totalToPay * STAFF[location]?.[selectedStaff]?.rate || 0);

    return (<>
      <Grid container justifyContent='space-between' alignItems='center' width='100%'>
        <Grid item>
          <Typography variant='h6'>{selectedStaff}</Typography>
        </Grid>
        <Grid item container gap={4} width='fit-content'>
          <Typography variant='body'>{t('commissions.pay.totalPayments', { amount: totalToPay.toFixed(2) })}</Typography>
          <Typography variant='body'><Trans i18nKey='commissions.pay.payableHeading' values={{ amount: commission }} components={{ b: <b /> }} /></Typography>
        </Grid>
      </Grid>
      <DataTable
        mb={2}
        loading={isPending('sales')}
        rows={Object.values(sales[selectedStaff])}
        columns={[
          { field: 'customer', headerName: t('commissions.columns.customer'), flex: 1 },
          { field: 'orderId', headerName: t('commissions.pay.columns.orderLink'), renderCell: params => OrderLink(params.value) },
          { 
            field: 'processedAt',
            headerName: t('commissions.pay.columns.paymentDate'),
            width: 200,
            valueGetter: params => new Date(params.value),
            renderCell: params => 
              new Intl.DateTimeFormat(dateLocaleFor(i18n.language), {
                timeZone: 'America/New_York',
                weekday: 'long',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              }).format(new Date(params.value)),
          },
          { field: 'totalPayment', headerName: t('commissions.pay.columns.totalPaid'), valueFormatter: params => `$${params.value.toFixed(2)}` },
          { field: 'payable', headerName: t('commissions.pay.columns.totalEligible'), valueFormatter: params => `$${params.value.toFixed(2)}` },
          { field: 'paid', headerName: t('commissions.pay.columns.pay'), renderCell: params => paySaleCheckbox(params) },
        ]}
        sortModel={[
          { field: 'processedAt', sort: 'asc' }
        ]}
        hideFooter={true}
        sx={{
          mb: 2,
          '.MuiDataGrid-row': {
            '&:hover &.Mui-selected:hover, &.Mui-selected': {
              backgroundColor: 'transparent',
            },
          }
        }}
      />
      <Divider sx={{ mb: '9px' }}/>
    </>)
  }

  const OrderLink = orderId => (
  <a
    href={`https://admin.shopify.com/store/delsonjewelry/orders/${orderId}`}
    target="_blank" 
    rel="noopener noreferrer"
    style={{ color: tokens.accent }}
  >
    {t('commissions.pay.orderLinkText')}
  </a>
  )

  const ConfirmSubmitPopup = () => (
    <Sheet
      open={showConfirmSubmit}
      onClose={() => setShowConfirmSubmit(false)}
      actions={
        <ActionBar
          cancel={{ label: t('common.cancel'), onClick: () => setShowConfirmSubmit(false) }}
          confirm={{ label: t('common.confirm'), onClick: handleSubmitPayout, type: 'submit' }}
        />
      }
    >
      <Typography variant='h6'>{t('commissions.pay.confirmSubmitMessage')}</Typography>
    </Sheet>
  )

  const AlertMessage = () => <Alert className='alert' severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>
  
  return (<>
    {showConfirmSubmit && ConfirmSubmitPopup()}
    <Page>
      {showAlert && AlertMessage()}
      {Header()}
      {showChangeLocation && ChangeLocationPopup()}
      {showChangeStaff && ChangeStaffPopup()}
      {Object.keys(sales).length ? SalesTables() : AllPaid()}
    </Page>
  </>)
}